/**
 * Use App Versions Hook
 *
 * Custom hooks for managing app versions with TanStack Query.
 * Handles caching, refetching, mutations, and error states automatically.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  fetchAppVersions,
  fetchAppVersionById,
  createAppVersion,
  updateAppVersion,
  deleteAppVersion,
  bulkUpdateAppVersions,
} from '@/lib/api';
import { useAuth } from '@/state/auth-context';
import type {
  AppVersion,
  AppVersionListParams,
  AppVersionListResponse,
  CreateAppVersionRequest,
  UpdateAppVersionRequest,
  DeleteAppVersionResponse,
  BulkUpdateAppVersionsRequest,
  BulkUpdateAppVersionsResponse,
} from '@/types/app-version';

/**
 * Use App Versions
 * Fetches paginated list of app versions with filters
 *
 * @param params - Query parameters for filtering and pagination
 * @returns TanStack Query result with app versions data
 */
export function useAppVersions(
  params: AppVersionListParams
): UseQueryResult<AppVersionListResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['appVersions', params],
    queryFn: async () => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchAppVersions(params, token);
    },
    staleTime: 30000, // 30 seconds - data is considered fresh for 30s
    gcTime: 300000, // 5 minutes - cache is garbage collected after 5 minutes
    retry: 2, // Retry failed requests twice
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnReconnect: true, // Refetch when network reconnects
  });
}

/**
 * Use App Version by ID
 * Fetches a single app version by its ID
 *
 * @param id - App version ID
 * @returns TanStack Query result with single app version data
 */
export function useAppVersionById(id: string | null): UseQueryResult<AppVersion> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['appVersion', id],
    queryFn: async () => {
      if (!id) {
        throw new Error('No app version ID provided');
      }

      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchAppVersionById(id, token);
    },
    enabled: id !== null, // Only run query if ID is provided
    staleTime: 10000, // 10 seconds - individual versions change more frequently
    gcTime: 300000, // 5 minutes - keep in cache
    retry: 2,
  });
}

/**
 * Use Create App Version
 * Mutation hook for creating a new app version
 *
 * @returns TanStack Mutation result
 */
export function useCreateAppVersion(): UseMutationResult<
  AppVersion,
  Error,
  CreateAppVersionRequest
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAppVersionRequest) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return createAppVersion(data, token);
    },
    onSuccess: () => {
      // Invalidate app versions list to refetch with new version
      queryClient.invalidateQueries({ queryKey: ['appVersions'] });
    },
  });
}

/**
 * Use Update App Version
 * Mutation hook for updating an existing app version
 *
 * @returns TanStack Mutation result
 */
export function useUpdateAppVersion(): UseMutationResult<
  AppVersion,
  Error,
  { id: string; data: UpdateAppVersionRequest }
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateAppVersionRequest }) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return updateAppVersion(id, data, token);
    },
    onSuccess: updatedVersion => {
      // Invalidate both the list and the specific version query
      queryClient.invalidateQueries({ queryKey: ['appVersions'] });
      queryClient.invalidateQueries({ queryKey: ['appVersion', updatedVersion.id] });
    },
  });
}

/**
 * Use Delete App Version
 * Mutation hook for soft deleting an app version
 *
 * @returns TanStack Mutation result
 */
export function useDeleteAppVersion(): UseMutationResult<DeleteAppVersionResponse, Error, string> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return deleteAppVersion(id, token);
    },
    onSuccess: data => {
      // Invalidate both the list and the specific version query
      queryClient.invalidateQueries({ queryKey: ['appVersions'] });
      queryClient.invalidateQueries({ queryKey: ['appVersion', data.id] });
    },
  });
}

/**
 * Use Bulk Update App Versions
 * Mutation hook for bulk activating or deactivating app versions
 *
 * @returns TanStack Mutation result
 */
export function useBulkUpdateAppVersions(): UseMutationResult<
  BulkUpdateAppVersionsResponse,
  Error,
  BulkUpdateAppVersionsRequest
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkUpdateAppVersionsRequest) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return bulkUpdateAppVersions(data, token);
    },
    onSuccess: data => {
      // Invalidate the list and all affected version queries
      queryClient.invalidateQueries({ queryKey: ['appVersions'] });
      data.ids.forEach(id => {
        queryClient.invalidateQueries({ queryKey: ['appVersion', id] });
      });
    },
  });
}
